// The theme preview (R806): one small live map that shows what a theme
// setting changes, drawn by the real renderer.
//
// The hub's MODDING card and the theme workshop page build ONE preview and
// share it between every row. A row calls `show(focus, look)`: the camera
// moves to the thing the row changes, and that thing plays on a loop — a
// torpedo run, damage figures, the arrival, the chart pieces, the territory
// changing hands. The four chrome foci run the real DOM modules in a frame
// over the canvas (`frame.ts`).
//
// **What it owns.** One `Engine` (one WebGL2 context), one `MapScene` over a
// staged world (`fixture.ts`), the arrival layer and the two hex views. It
// opens no socket, plays no sound and reads no setting: the caller hands it
// the look, and the caller owns the audio.
//
// **What it costs** (ui-map-interface.md §16.7, R806):
//
// - the frame loop runs at the menu's own rate, `MENU_FRAME_MS`, and not at
//   the game's 60 fps;
// - it stops while the caller paused it, while the document is hidden and
//   while the frame is off screen;
// - the canvas takes its container's size at the device pixel ratio, capped
//   at 2 by the context;
// - `destroy` stops the loop, frees every GPU object, loses the context on
//   purpose and puts the live grid back, so a hub page keeps no hidden
//   context alive.
//
// **The grid.** The engine's grid is the module grid of `world.ts`, and a
// hello sets it. The preview's hello sets the 18 × 12 board, so the LAST
// preview to go sets back the grid the first one found. Two previews can
// stand at once (the close-up of R1914, the picture baker of R1909), and a
// preview that goes first must not take the board from the other. Nothing
// else on the hub page reads the live grid.
//
// **The close-up** (R1914). `zoom` over 1 brings the camera that many
// times nearer than the focus's own frame (`closeTarget`), and `mapOnly`
// shows the canvas alone: the frame is bare (`frame.ts`). `maxFps` caps
// the loop of a preview under the menu rate.
//
// **The picture baker** (R1918). `snap` puts the camera on the pose of a
// new focus at once, with no ease, so a baker waits for the content of a
// job and not for the camera. `zoom` may differ by focus: a picture of
// one mark comes nearer than a picture whose thing reaches wide. `fast`
// runs the loop at a multiple of the menu rate with a clock as many times
// as fast (R2084): each frame steps the clock as a menu frame does, so a
// picture shows what the menu preview shows, in less wall time.

import { createEngine, type Engine, type EngineOpts } from '../fast/engine';
import { MapScene, STILL_CAMERA, stillVisibility, type SceneVisibility } from '../fast/scene';
import { SpawnLayer } from '../fast/layers/spawn';
import { HexBorderLayer, HexFillLayer } from '../fast/layers/hexgrid';
import type { FrameInfo } from '../fast/layer';
import type { ChartHost } from '../fast/chart/furniture';
import { DEFAULT_KNOBS } from '../fast/knobs';
import { GameState } from '../state';
import { HeatmapKind, State } from '../gen/proto/aquaphobia';
import { MENU_FRAME_MS } from '../gen/presentation';
import { DEFAULT_SETTINGS } from '../ui/settings';
import { fast as SF } from '../gen/strings';
import { COLS, ROWS, setGrid as setWorldGrid } from '../world';
import {
  FLIP_CELLS, HOLD_CELL, OWN_HOLD_SITE, PREVIEW_COLS, PREVIEW_FACTION, PREVIEW_GROUPS,
  PREVIEW_MAGAZINE, PREVIEW_MATE, PREVIEW_ROUND_KIND, PREVIEW_ROUND_NAME, PREVIEW_ROWS,
  centreOf, closeTarget, damageEvents, detonationKindAt, flipOwner, focusTarget, poseFor,
  previewCurrents, previewDepth, previewFleets, previewHello, previewMarkers, previewPois, previewSieges, torpedoAt, torpedoHit,
  FLEET_SAPPER, SAPPER_FOCI, sapperFleet, seaStage,
  FLEET_SEEKER, seekerFleet,
  type ViewPose,
} from './fixture';
import { HIT_FIELDS, SHOT_FIELDS, type HitField, type ShotField } from '../render/combat-styles';
import { readSceneLook } from '../render/scene-look';
import {
  defaultPreviewLook, territoryLayerView,
  type PreviewFocus, type PreviewLook,
} from './look';
import { PreviewFrame } from './frame';

/**
 * The three shot rows and the three hit rows of the WORLD tab (R1007): a
 * focus that names one of the six World fields of `render/combat-styles.ts`.
 */
const SHOT_FOCI: ReadonlySet<string> = new Set(SHOT_FIELDS);
const HIT_FOCI: ReadonlySet<string> = new Set(HIT_FIELDS);
const isShotFocus = (f: PreviewFocus): f is PreviewFocus & ShotField => SHOT_FOCI.has(f);
const isHitFocus = (f: PreviewFocus): f is PreviewFocus & HitField => HIT_FOCI.has(f);

/** The frame cap: the menu's own rate, `[presentation.menu] frame_ms`,
 *  about 30 fps. */
export const PREVIEW_MAX_FPS = 1000 / MENU_FRAME_MS;

/** How often the staged fleets send a state frame: the wire's 10 Hz. */
const FEED_SEC = 0.1;
/** How fast the camera reaches a new focus: the time constant, in ms. */
const CAMERA_EASE_MS = 180;
/** The rest between two arrivals of the spawn focus. */
const SPAWN_REST_SEC = 1.2;

/** Builds the engine. The shipped path passes `createEngine`. */
export type PreviewEngineFactory = (canvas: HTMLCanvasElement, opts: EngineOpts) => Promise<Engine>;

export interface ThemePreviewOpts {
  /** The engine factory. A test hands over a recording engine. */
  createEngine?: PreviewEngineFactory;
  /** The canvas alone: no chrome and no DOM card over the map (R1914). */
  mapOnly?: boolean;
  /** How many times nearer than the focus's own frame the camera stands;
   *  1 by default (R1914). A function gives each focus its own (R1918). */
  zoom?: number | ((focus: PreviewFocus) => number);
  /** The camera stands on the pose of each focus at once, with no ease
   *  (the picture baker, R1918). */
  snap?: boolean;
  /** The frame cap; `PREVIEW_MAX_FPS` by default, and never over it. */
  maxFps?: number;
  /** Runs the loop `fast` times as often as the menu rate, with a clock
   *  `fast` times as fast, so each frame steps the clock as a menu frame
   *  does (the picture baker, R2084). It lifts the cap of `maxFps`. */
  fast?: number;
}

/** How many previews hold the board now, and the grid the first one found. */
let boardHolders = 0;
let pageGrid: { cols: number; rows: number } | null = null;

/**
 * The rows that show a piece of chart furniture: the map markers (card 66),
 * the current field and its heat look (card 07, R2716), the depth tints
 * (card 09, R2726) and the points of interest (R2714). A reader whose chart
 * reading is `off` would see nothing on them, so each draws the default
 * reading, in the MODDING tab and in the workshop alike, as the chart row
 * does (R815).
 */
const FURNITURE_FOCI: ReadonlySet<PreviewFocus> = new Set<PreviewFocus>([
  'mapMarker', 'currents', 'currentsHeat', 'depth', 'poi',
]);

/** No current field: what the board carries on every row but `currents`
 *  and `currentsHeat`. */
const NO_CURRENTS = new Uint8Array(0);
/** No band field: what the board carries on every row but `depth`. */
const NO_DEPTH = new Uint8Array(0);

export class ThemePreview {
  /**
   * Resolves true when the engine is up and the world is built, and false
   * when the browser gave no WebGL2 context or the preview was destroyed
   * first. The frame says so in the first case.
   */
  readonly ready: Promise<boolean>;

  private readonly frame: PreviewFrame;
  private readonly state = new GameState();
  private readonly hello = previewHello();
  /** The staged current field, laid on the board by the `currents` and
   *  the `currentsHeat` focus alone (R2716, R2869). */
  private readonly currents = previewCurrents();
  /** The staged band field, laid on the board by the `depth` focus alone
   *  (R2726). */
  private readonly depthBands = previewDepth();
  private engine: Engine | null = null;
  private scene: MapScene | null = null;
  private spawn: SpawnLayer | null = null;
  private hexFills: HexFillLayer | null = null;
  private hexBorders: HexBorderLayer | null = null;
  private observer: IntersectionObserver | null = null;

  private focusNow: PreviewFocus = 'territory';
  private look: PreviewLook = defaultPreviewLook();
  private readonly vis: SceneVisibility = stillVisibility();

  private userPaused = false;
  private onScreen = true;
  private live = false;
  private dead = false;
  /** The preview counts among the holders of the board. */
  private holdsBoard = false;
  /** The close-up's factor over the focus's own frame (R1914). */
  private readonly zoomOf: (focus: PreviewFocus) => number;
  /** The camera takes no ease (R1918). */
  private readonly snap: boolean;

  /** Seconds since the focus was shown. Every loop reads this clock. */
  private clock = 0;
  private feedAt = -1;
  private inFlight = false;
  private flipped = 0;
  private spawnRest = -1;
  private pose: ViewPose | null = null;
  /** One reused point: the hold in CSS px, which the arrival reads. */
  private readonly homePx = { x: 0, y: 0 };
  /** The ids of the sea objects the stage sent last (R2657). */
  private readonly seaIds = new Set<number>();
  /** The staged Sapper stands on the board. */
  private seaOn = false;
  /** The staged SEARCH of the CACHES row stands on the board (R2833). */
  private seekerOn = false;

  constructor(host: HTMLElement, opts: ThemePreviewOpts = {}) {
    this.frame = new PreviewFrame(host, { bare: opts.mapOnly === true });
    const z = opts.zoom ?? 1;
    this.zoomOf = typeof z === 'function' ? (f) => Math.max(1, z(f)) : () => Math.max(1, z);
    this.snap = opts.snap === true;
    const make = opts.createEngine ?? createEngine;
    const fast = Math.max(1, opts.fast ?? 1);
    this.ready = make(this.frame.canvas, {
      maxFps: fast > 1 ? PREVIEW_MAX_FPS * fast : Math.min(PREVIEW_MAX_FPS, opts.maxFps ?? PREVIEW_MAX_FPS),
      clockScale: fast,
      debugHandle: false,
      onFrame: (f) => this.onFrame(f),
    }).then((engine) => {
      if (this.dead) { this.release(engine); return false; }
      this.build(engine);
      return true;
    }, (err: unknown) => {
      console.warn('preview: the engine did not start', err);
      if (!this.dead) this.frame.showNoGl(SF.no_webgl2);
      return false;
    });
  }

  /** The focus shown now. */
  get focus(): PreviewFocus { return this.focusNow; }

  /** Whether the frame loop runs now. */
  get running(): boolean { return this.live; }

  /**
   * Shows what one row changes, in one look. A call before the engine is
   * up is kept and shown when it is. A call with the same focus restarts
   * that focus's loop in the new look.
   */
  show(focus: PreviewFocus, look: PreviewLook): void {
    if (this.dead) return;
    this.focusNow = focus;
    this.look = look;
    this.applyShow();
  }

  /** Stops the frame loop until `resume`. The caller's pause wins over all. */
  pause(): void {
    this.userPaused = true;
    this.sync();
  }

  /** Lets the frame loop run again, while the frame is visible. */
  resume(): void {
    this.userPaused = false;
    this.sync();
  }

  /**
   * Takes the preview down for good: the loop, every listener, the frame,
   * every GPU object and the context itself. The live grid goes back to the
   * grid the page had.
   */
  destroy(): void {
    if (this.dead) return;
    this.dead = true;
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.observer?.disconnect();
    this.observer = null;
    this.frame.destroy();
    if (this.engine) this.release(this.engine);
    this.engine = null;
    this.scene = null;
    this.spawn = null;
    this.hexFills = null;
    this.hexBorders = null;
  }

  // -- the build ---------------------------------------------------------------

  private build(engine: Engine): void {
    this.engine = engine;
    if (boardHolders === 0) pageGrid = { cols: COLS, rows: ROWS };
    boardHolders++;
    this.holdsBoard = true;
    const chartHost: ChartHost = {
      root: this.frame.furniture,
      hover: () => null,
      hoverAt: () => null,
      hoverCell: () => -1,
      aim: () => null,
      // The frame's dock column moves a corner piece (R1234).
      onCluster: (fn) => this.frame.onLayout(fn),
      // The staged seeded cache shows on the row of the points of interest
      // (R2714), as it does in a world that plays the treasure.
      cachesOn: () => true,
    };
    const scene = new MapScene(engine, {
      state: this.state,
      camera: STILL_CAMERA,
      cue: () => { /* the caller owns the sound */ },
      torpedoStyle: this.look.torpedo,
      kraken: false,
      chartHost,
      onFlip: () => {
        this.hexFills?.invalidate();
        this.hexBorders?.invalidate();
      },
    });
    this.scene = scene;
    // The two page layers the preview needs beside the scene: the arrival
    // and the two hex views. The slots are the engine's.
    const spawn = new SpawnLayer(engine, engine);
    spawn.onEnd = () => { if (this.focusNow === 'spawn') this.spawnRest = SPAWN_REST_SEC; };
    this.spawn = spawn;
    const fills = new HexFillLayer(engine);
    const borders = new HexBorderLayer(engine);
    fills.setVisible(false);
    borders.setVisible(false);
    this.hexFills = fills;
    this.hexBorders = borders;
    // An effect swap bakes new cells and can grow the atlas, which moves the
    // hex cell. The listener goes with the engine, so nothing unsubscribes.
    // A test engine carries no atlas.
    engine.atlas?.onRepacked(() => { fills.invalidate(); borders.invalidate(); });
    engine.setLayer('spawn', spawn);
    engine.setLayer('hexfills', fills);
    engine.setLayer('hexborders', borders);

    // The staged world, in the hello order of `MapScene`.
    scene.applyKnobs(DEFAULT_KNOBS);
    this.state.setHello(this.hello);
    scene.applyHello(this.hello);
    fills.setOwners(scene.owners, scene.blocked);
    borders.setOwners(scene.owners, scene.blocked);
    scene.creatures.setBlocked(scene.blocked);
    scene.fleets.setGroups(PREVIEW_GROUPS);
    // The staged Lancer's tubes (R1474): the hello carries no ship table,
    // so the preview states the magazine and the round itself.
    scene.fleets.setRounds(() => ({
      mag: PREVIEW_MAGAZINE, kind: PREVIEW_ROUND_KIND, name: PREVIEW_ROUND_NAME,
    }));
    // The squad roster lands before the first frame, so a squad hull and a
    // squad hold wear their rank from the first picture the viewer sees. A
    // live world gets the roster in the clan frame, after the hello.
    scene.setSquadIds(new Set([PREVIEW_MATE]), new Map([[PREVIEW_MATE, 2]]));
    // The Sieges frame names what each strategic object IS (R1104). It
    // lands once, because the staged objects never change hands: the site
    // layer KEEPS the kind, the contested flag and the attacker, so a later
    // `setLook` re-keys the two marks without it.
    scene.setSieges(previewSieges());
    scene.applyState(State.fromPartial({
      full: true, fleets: previewFleets(0, this.look.effects),
    }));
    scene.fleets.snap();

    // The loop's own visibility listener was added when the engine was
    // built, so this one runs after it and has the last word.
    document.addEventListener('visibilitychange', this.onVisibility);
    if (typeof IntersectionObserver !== 'undefined') {
      this.observer = new IntersectionObserver((records) => {
        for (const r of records) this.onScreen = r.isIntersecting;
        this.sync();
      });
      this.observer.observe(this.frame.wrap);
    }
    this.applyShow();
    this.sync();
  }

  private release(engine: Engine): void {
    engine.stop();
    this.scene?.dispose();
    engine.dispose();
    // `dispose` frees every object the engine made. Losing the context
    // hands the context itself back to the browser now, and not at the
    // next collection.
    engine.gfx.loseContext();
    if (this.holdsBoard) {
      this.holdsBoard = false;
      boardHolders--;
      const g = pageGrid;
      if (boardHolders === 0) {
        pageGrid = null;
        if (g && COLS === PREVIEW_COLS && ROWS === PREVIEW_ROWS) setWorldGrid(g.cols, g.rows);
      }
    }
    this.live = false;
  }

  // -- the show ----------------------------------------------------------------

  /** Writes the look onto the scene and restarts the focus's loop. */
  private applyShow(): void {
    const scene = this.scene, spawn = this.spawn;
    const look = this.look, focus = this.focusNow;
    this.frame.show(focus, look, this.hello.worldName);
    if (!scene || !spawn) return;
    scene.setTorpedoStyle(look.torpedo);
    scene.damage.setStyle(look.damage);
    // The canvas half of the look (R814). The preview writes it on every
    // show, because a row of the workshop changes one field at a time. The
    // scene fields of a preview look carry the scene's own names, and a
    // field the look leaves out takes the AUTO value, as `ui/look.ts
    // sceneLookOf` gives a resolved look.
    //
    // The faction key is a card the size of a corner of the frame (R1568),
    // so it stands on its own row alone and covers no other row's mark.
    const sceneLook = readSceneLook({
      ...look, factionKey: focus === 'factionKey' ? look.factionKey : 'off',
    });
    scene.setLook(sceneLook);
    // The hex border takes the faction edge look too (R3277).
    this.hexBorders?.setEdge(sceneLook.factionEdge);
    scene.chart?.setOverlay(look.chartPieces ?? null);
    // The hold effect rides an owner frame, as it does in the game (R910):
    // the hold stands in the hello, so a new pick reaches the layer through
    // the one message that states the field.
    scene.setSiteOwner(OWN_HOLD_SITE, PREVIEW_FACTION, 0, 0, look.effects?.hold ?? 0);
    // A row of chart furniture draws the default reading where the reader's
    // reading is `off`, or it would show nothing (`FURNITURE_FOCI`).
    this.vis.chart = look.chart === 'off' && FURNITURE_FOCI.has(focus) ? DEFAULT_SETTINGS.chart : look.chart;
    // The current field is chart furniture too (card 07, R2716), and it lies
    // on the board only while its row shows, so every other row keeps the
    // picture it had. The store reads the field of the hello it holds.
    // The `currentsHeat` row turns the CURRENTS heat map on in the state of
    // the board (R2869, R2870), so the scene draws the heat look as the
    // active look. That map hides the depth tints, and the band field below
    // lies on the DEPTH row alone, so this row shows none.
    const currentsRow = focus === 'currents' || focus === 'currentsHeat';
    this.state.setHeatmap(focus === 'currentsHeat'
      ? HeatmapKind.HEATMAP_KIND_CURRENTS : HeatmapKind.HEATMAP_KIND_NONE);
    this.hello.currents = currentsRow ? this.currents : NO_CURRENTS;
    // The depth tints are chart furniture too (card 09, R2726): the band
    // field lies on the board only while the DEPTH row shows. The survey
    // reason below hands the new field to the depth layer.
    this.hello.depth = focus === 'depth' ? this.depthBands : NO_DEPTH;
    // The staged markers (R1291), stamped on every show, so the ages of the
    // rows stay the ages of the cards however long the preview stays open.
    this.state.setMapMarkers(previewMarkers(Date.now() / 1000));
    // The staged points of interest (R2714) stand only while their row
    // shows, so every other picture stays the picture it was. The hello
    // carries no survey, so every cell reads surveyed.
    this.state.survey.fromHello({ survey: new Uint8Array(0), pois: focus === 'poi' ? previewPois() : [] });
    scene.chart?.invalidate('survey');
    this.vis.holdSymbol = look.hold;
    const lv = territoryLayerView(look.territory);
    scene.territory.setFills(lv.sovFills);
    scene.territory.setBorders(lv.sovBorders);
    scene.terrain.setVisible(lv.terrain);
    this.hexFills?.setVisible(lv.hexFills);
    this.hexBorders?.setVisible(lv.hexBorders);

    // Every loop starts again from its first beat.
    this.clock = 0;
    this.feedAt = -1;
    this.inFlight = false;
    scene.torpedoes.clear();
    scene.damage.clear();
    // The six gun rows (R1007). The staged fight is one pair of hulls, so the
    // preview names the FIELD the row shows in place of making an artillery
    // hull fire or stripping a shield.
    scene.combat.clear();
    scene.combat.setPreviewFields(isShotFocus(focus) ? focus : '', isHitFocus(focus) ? focus : '');
    if (this.flipped !== 0) this.flip(0);
    // The Sapper part stands on the board only while one of its rows shows
    // (R2657), so every other row keeps the picture it had.
    if (!SAPPER_FOCI.has(focus)) this.clearSea();
    // The staged SEARCH stands on the board only while the CACHES row
    // shows (R2833).
    if (focus !== 'cacheWork' && this.seekerOn) {
      this.scene?.applyState(State.fromPartial({ removed: [FLEET_SEEKER] }));
      this.seekerOn = false;
    }
    scene.sapperWork.clear();
    this.spawnRest = -1;
    if (focus === 'spawn') this.startSpawn();
    else spawn.skip();
  }

  /**
   * One frame of the Sapper part (R2657): the fleets with the staged
   * Sapper, the rows of the stage and the ids that left it. The first frame
   * of a still stage restates the picture, so the store reads no lay off
   * rows that were there; a lay stage sends deltas alone.
   */
  private feedSea(t: number): void {
    const focus = this.focusNow;
    const stage = seaStage(focus, t);
    const lay = focus === 'seaMineLay' || focus === 'deployableLay';
    const full = !lay && (stage.full || this.seaIds.size === 0);
    const now = new Set(stage.rows.map((r) => r.id));
    const gone = full ? [] : [...this.seaIds].filter((id) => !now.has(id));
    this.seaIds.clear();
    for (const id of now) this.seaIds.add(id);
    this.seaOn = true;
    this.scene?.applyState(State.fromPartial({
      full, fleets: [...previewFleets(t, this.look.effects), sapperFleet(stage)],
      objects: stage.rows, objectsGone: gone,
    }));
  }

  /** Takes the Sapper and its objects off the board again. */
  private clearSea(): void {
    if (!this.seaOn) return;
    this.scene?.applyState(State.fromPartial({ removed: [FLEET_SAPPER], objectsGone: [...this.seaIds] }));
    this.seaIds.clear();
    this.seaOn = false;
  }

  /** The two frontier cells go to `to`, as a state frame's flips. */
  private flip(to: number): void {
    const from = this.flipped;
    this.flipped = to;
    this.scene?.applyState(State.fromPartial({
      flips: FLIP_CELLS.map((index) => ({ index, from, to })),
    }));
  }

  /** Plays the arrival in the look's style, as a login. */
  private startSpawn(): void {
    const engine = this.engine, scene = this.scene, spawn = this.spawn;
    if (!engine || !scene || !spawn) return;
    const home = centreOf(HOLD_CELL);
    const s = scene.spawnHost(() => {
      const z = engine.view.scale.x;
      this.homePx.x = home.x * z + engine.view.x;
      this.homePx.y = home.y * z + engine.view.y;
      return this.homePx;
    });
    spawn.start(this.look.spawn, 'login', s);
  }

  // -- the frame ---------------------------------------------------------------

  private onFrame(f: FrameInfo): void {
    const engine = this.engine, scene = this.scene, spawn = this.spawn;
    if (!engine || !scene || !spawn) return;
    const dt = f.dtMs / 1000;
    const t0 = this.clock;
    const t = (this.clock += dt);
    this.moveCamera(engine, f.dtMs);

    if (this.feedAt < 0 || t - this.feedAt >= FEED_SEC) {
      this.feedAt = t;
      if (SAPPER_FOCI.has(this.focusNow)) this.feedSea(t);
      else if (this.focusNow === 'cacheWork') {
        this.seekerOn = true;
        scene.applyState(State.fromPartial({ fleets: [...previewFleets(t, this.look.effects), seekerFleet(t)] }));
      } else scene.applyState(State.fromPartial({ fleets: previewFleets(t, this.look.effects) }));
    }
    switch (this.focusNow) {
      // The torpedo effect row needs a round in flight to wear its pick (R910).
      // The detonation row (R2079) runs the same flight, one round of the
      // three kinds a run, so the look shows three splashes in turn.
      case 'torpedo':
      case 'detonation':
      case 'effectTorpedo': {
        const kind = this.focusNow === 'detonation' ? detonationKindAt(t) : undefined;
        const round = torpedoAt(t, this.look.effects, kind);
        if (round) {
          scene.torpedoes.apply([round]);
          this.inFlight = true;
        } else if (this.inFlight) {
          this.inFlight = false;
          scene.torpedoes.clear();
          scene.applyEvents([torpedoHit(t, kind)]);
        }
        break;
      }
      // The damage row and the six gun rows all run the damage loop: it is the
      // one loop that raises real HIT events, and the gun looks are what those
      // events draw (R1007).
      case 'damage':
      case 'shotMelee': case 'shotArtillery': case 'shotSentinel':
      case 'hitShield': case 'hitHull': case 'hitSink': {
        const events = damageEvents(t0, t);
        if (events.length > 0) scene.applyEvents(events);
        break;
      }
      case 'territory': {
        const owner = flipOwner(t);
        if (owner !== this.flipped) this.flip(owner);
        break;
      }
      case 'spawn':
        if (this.spawnRest >= 0) {
          this.spawnRest -= dt;
          if (this.spawnRest <= 0) {
            this.spawnRest = -1;
            this.startSpawn();
          }
        }
        break;
      default:
        break;
    }
    this.frame.tick(dt);
    scene.frame(f, this.vis);
    // The round's jolt and the arrival's shudder, as the game adds them.
    engine.view.shake.x = scene.shakeX + spawn.shakeX;
    engine.view.shake.y = scene.shakeY + spawn.shakeY;
    // The arrival's zoom nudge (R838), copied the same way.
    engine.view.nudge.k = spawn.nudgeK;
    engine.view.nudge.ax = spawn.nudgeAx;
    engine.view.nudge.ay = spawn.nudgeAy;
  }

  /** Eases the view toward the pose of the focus, for the surface's size now. */
  private moveCamera(engine: Engine, dtMs: number): void {
    const w = engine.gfx.width, h = engine.gfx.height;
    if (w <= 1 || h <= 1) return;
    const own = focusTarget(this.focusNow);
    const k = this.zoomOf(this.focusNow);
    const want = poseFor(k > 1 ? closeTarget(own, k) : own, w, h);
    let p = this.pose;
    if (!p || this.snap) {
      p = this.pose = want;
    } else {
      const k = 1 - Math.exp(-dtMs / CAMERA_EASE_MS);
      p.zoom += (want.zoom - p.zoom) * k;
      p.x += (want.x - p.x) * k;
      p.y += (want.y - p.y) * k;
    }
    engine.view.scale.set(p.zoom);
    engine.view.x = p.x;
    engine.view.y = p.y;
  }

  // -- the loop's life -----------------------------------------------------------

  private onVisibility = (): void => { this.sync(); };

  /** Runs the loop exactly while the caller, the document and the page allow it. */
  private sync(): void {
    const engine = this.engine;
    if (!engine) return;
    const run = !this.dead && !this.userPaused && this.onScreen && !document.hidden;
    if (run) engine.start();
    else engine.stop();
    this.live = run;
  }
}
